<script setup>
import { computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { OnClickOutside } from '@vueuse/components';
import useVuelidate from '@vuelidate/core';

import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Breadcrumb from 'next/breadcrumb/Breadcrumb.vue';
import Editor from 'next/wgpt/Automation/editor/Index.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();

const eventAutomationId = computed(() =>
  Number(route.params.eventAutomationId)
);
const initialState = {};
const state = ref({ ...initialState });
const v$ = useVuelidate({}, state);
const showActivateActionMenu = ref(false);

const uiFlags = useMapGetter('wgptEventAutomations/getUIFlags');

const eventAutomation = computed(() =>
  getters['wgptEventAutomations/getEventAutomation'].value(
    eventAutomationId.value
  )
);

const triggerType = computed(
  () => state.value?.trigger?.type || 'custom_event_logged'
);

const hasPendingChanges = computed(() => {
  if (!eventAutomation.value) return false;
  return (
    state.value?.name !== eventAutomation.value.name ||
    state.value?.description !== eventAutomation.value.description ||
    JSON.stringify(state.value?.steps) !==
      JSON.stringify(eventAutomation.value.steps) ||
    JSON.stringify(state.value?.trigger) !==
      JSON.stringify(eventAutomation.value.trigger)
  );
});

const isLoading = computed(() => {
  if (eventAutomation.value?.id === eventAutomationId.value) return false;
  return uiFlags.value.isFetchingItem;
});

const isActive = computed(() => eventAutomation.value?.active);

const executeMenuItems = computed(() => [
  {
    label: t(
      'WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.HEADER.DEACTIVATE_BUTTON'
    ),
    value: 'deactivate',
    action: 'deactivate',
    disabled:
      isLoading.value ||
      uiFlags.value.isUpdating ||
      hasPendingChanges.value ||
      !isActive.value,
  },
]);

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t(
        'WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.BREADCRUMB.ENRICHMENTS'
      ),
      link: '#',
    },
  ];
  if (eventAutomation.value?.name) {
    items.push({ label: eventAutomation.value.name });
  }
  return items;
});

const buildState = record => ({
  name: record?.name || '',
  description: record?.description || '',
  trigger: {
    ...(record?.trigger || { type: 'custom_event_logged' }),
    attributes: {
      ...(record?.trigger?.attributes || {}),
    },
  },
  steps: record?.steps || { v: [] },
});

watch(
  eventAutomationId,
  (newId, oldId) => {
    if (!newId || newId === oldId) return;
    store.dispatch('wgptEventAutomations/show', newId);
  },
  { immediate: true }
);

watch(
  eventAutomation,
  (newRecord, oldRecord = {}) => {
    if (Object.keys(oldRecord).length) return;
    state.value = buildState(newRecord);
  },
  { immediate: true }
);

const activationErrorMessage = () => {
  if (
    triggerType.value === 'event_enrichment_scheduled' &&
    !state.value.trigger?.attributes?.cron
  ) {
    return t(
      'WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.VALIDATION.CRON_REQUIRED'
    );
  }

  return null;
};

const handleSave = async () => {
  try {
    if (v$.value.$invalid) {
      v$.value.$touch();
      throw new Error(
        t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.ERROR_MESSAGE')
      );
    }

    const updateData = {
      id: eventAutomationId.value,
      wgpt_automation_attributes: {
        name: state.value.name,
        description: state.value.description,
        trigger: state.value.trigger,
        steps: state.value.steps,
      },
    };

    const updated = await store.dispatch(
      'wgptEventAutomations/update',
      updateData
    );
    useAlert(
      t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.SUCCESS_MESSAGE')
    );

    if (updated) {
      state.value = buildState(updated);
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.ERROR_MESSAGE')
    );
  }
};

const goToList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(
      `/app/accounts/${route.params.accountId}/settings/custom-reports/enrichments`
    );
  }
};

const toggleActivate = async active => {
  if (isActive.value === active) return;

  const validationError = active ? activationErrorMessage() : null;
  if (validationError) {
    useAlert(validationError);
    return;
  }

  try {
    await store.dispatch('wgptEventAutomations/update', {
      id: eventAutomationId.value,
      wgpt_automation_attributes: { active },
    });
    useAlert(
      t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.ERROR_MESSAGE')
    );
  }
};

const handleActivateAction = ({ action }) => {
  showActivateActionMenu.value = false;
  if (action === 'deactivate') toggleActivate(false);
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-5">
            <Breadcrumb :items="breadcrumbItems" @click="goToList" />
          </div>
          <div class="flex gap-2 items-center">
            <Button
              :label="
                t(
                  'WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.HEADER.SAVE_BUTTON'
                )
              "
              :is-loading="uiFlags.isUpdating"
              :disabled="isLoading || uiFlags.isUpdating || !hasPendingChanges"
              size="sm"
              @click="handleSave"
            />

            <div class="flex items-center">
              <Button
                :label="
                  t(
                    'WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.HEADER.ACTIVATE_BUTTON'
                  )
                "
                size="sm"
                class="ltr:rounded-r-none rtl:rounded-l-none"
                :disabled="
                  isLoading ||
                  uiFlags.isUpdating ||
                  hasPendingChanges ||
                  isActive
                "
                @click="toggleActivate(true)"
              />
              <div class="relative">
                <OnClickOutside @trigger="showActivateActionMenu = false">
                  <Button
                    icon="i-lucide-chevron-down"
                    size="sm"
                    :disabled="
                      !eventAutomationId || hasPendingChanges || !isActive
                    "
                    class="ltr:rounded-l-none rtl:rounded-r-none"
                    @click.stop="
                      showActivateActionMenu = !showActivateActionMenu
                    "
                  />
                  <DropdownMenu
                    v-if="showActivateActionMenu"
                    :menu-items="executeMenuItems"
                    class="mt-2 ltr:right-0 rtl:left-0 top-full"
                    @action="handleActivateAction($event)"
                  />
                </OnClickOutside>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
    <main class="flex-1 overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <Editor
        v-else
        v-model="state"
        :dirty="hasPendingChanges"
        class="h-full border-t border-n-weak"
        disable-trigger
      />
    </main>
  </section>
</template>
